import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import {
  GET_ALL_DESKTOP_SETTINGS_QUERY_KEY,
  type GetAllDesktopSettingsData,
  useSaveWidgetsMutation
} from 'shared/api/containerApi'
import type { PersistedWidgetSpec } from 'shared/types/widgets'

import {
  buildWidgetOrderSavePayload,
  canPersistWidgetOrder
} from '../utils/buildWidgetOrderSavePayload'
import { selectDesktopWidgets } from '../utils/desktopWidgetAdapter'
import { setDesktopWidgetsQueryData } from '../utils/setDesktopWidgetsQueryData'
import { transformWidgetsForSave } from '../utils/transformWidgetsForSave'
import {
  enqueueWidgetWrite,
  getWidgetWriteQueue
} from '../utils/widgetWriteQueue'
import { useDesktopWidgets } from './useDesktopWidgets'

type MutationCallbacks = {
  onSuccess?: () => void
  onError?: () => void
}

type WidgetEdit = (
  current: PersistedWidgetSpec[],
  canReorder: boolean
) => {
  nextWidgets: PersistedWidgetSpec[]
  widgetsToSave?: PersistedWidgetSpec[]
} | null

/**
 * Shared optimistic writer for widget upserts. Writes from separate hook
 * instances share one queue, so an older save cannot finish after a newer one.
 * A successful save keeps the optimistic order: an immediate read can still
 * return the server's previous order and move a dropped widget again.
 */
export const useWidgetMutations = () => {
  const queryClient = useQueryClient()
  const { items, ...query } = useDesktopWidgets()
  const saveWidgetsMutation = useSaveWidgetsMutation()
  const { isPending, mutateAsync } = saveWidgetsMutation

  const editWidgets = useCallback(
    (edit: WidgetEdit, callbacks: MutationCallbacks = {}) => {
      const previousData = queryClient.getQueryData<GetAllDesktopSettingsData>([
        GET_ALL_DESKTOP_SETTINGS_QUERY_KEY
      ])
      const current = selectDesktopWidgets(previousData)
      const canReorder = canPersistWidgetOrder(previousData)
      const result = edit(current, canReorder)
      if (!result) return
      const { nextWidgets, widgetsToSave } = result
      const payload = widgetsToSave
        ? transformWidgetsForSave(widgetsToSave, previousData?.widgets)
        : canReorder && previousData
          ? buildWidgetOrderSavePayload(previousData, nextWidgets)
          : null
      if (!payload) return
      const queue = getWidgetWriteQueue(queryClient)
      queue.revision += 1
      const revision = queue.revision
      queue.latestWidgets = nextWidgets

      setDesktopWidgetsQueryData(queryClient, nextWidgets)

      void enqueueWidgetWrite(queryClient, async () => {
        await queryClient.cancelQueries({
          queryKey: [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY]
        })
        // Cancellation can revert a query to its last result. Reapply the
        // newest optimistic state, including edits queued while we waited.
        setDesktopWidgetsQueryData(queryClient, queue.latestWidgets)

        let saved = false
        try {
          await mutateAsync({ widgets: payload })
          saved = true
        } catch {
          // Writes are serialized; this flag must survive newer queued edits.
          // eslint-disable-next-line require-atomic-updates
          queue.needsReconcile = true
          if (revision === queue.revision && previousData) {
            queryClient.setQueryData(
              [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY],
              previousData
            )
          }
          callbacks.onError?.()
        } finally {
          if (revision === queue.revision && queue.needsReconcile) {
            queue.needsReconcile = false
            await queryClient.invalidateQueries({
              queryKey: [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY]
            })
            // A newer edit may have arrived while the read-back was in flight.
            // Its optimistic order remains authoritative until its own save.
            if (revision !== queue.revision) {
              setDesktopWidgetsQueryData(queryClient, queue.latestWidgets)
            }
          }
        }
        if (saved) callbacks.onSuccess?.()
      }).catch(() => {
        void queryClient
          .invalidateQueries({
            queryKey: [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY]
          })
          .catch(() => {})
      })
    },
    [mutateAsync, queryClient]
  )

  const replaceWidgets = useCallback(
    (
      nextWidgets: PersistedWidgetSpec[],
      widgetsToSave: PersistedWidgetSpec[] = nextWidgets,
      callbacks: MutationCallbacks = {}
    ) => {
      editWidgets(
        () => ({
          nextWidgets,
          ...(widgetsToSave === nextWidgets ? {} : { widgetsToSave })
        }),
        callbacks
      )
    },
    [editWidgets]
  )

  const updateWidget = useCallback(
    (
      id: string,
      update: (widget: PersistedWidgetSpec) => PersistedWidgetSpec,
      callbacks: MutationCallbacks = {}
    ) => {
      editWidgets(current => {
        let changedWidget: PersistedWidgetSpec | undefined
        const nextWidgets = current.map(widget => {
          if (widget.id !== id) return widget
          changedWidget = update(widget)
          return changedWidget
        })
        if (!changedWidget) return null
        return {
          nextWidgets,
          widgetsToSave: [changedWidget]
        }
      }, callbacks)
    },
    [editWidgets]
  )

  return {
    ...query,
    items,
    canReorder: canPersistWidgetOrder(query.data),
    isSaving: isPending,
    editWidgets,
    replaceWidgets,
    updateWidget
  }
}
